<template>
    <div class="container-fluid p-4">
        <TagSelector optionName="skills" availableOptionsHeading="All Skills"
            :selectedOptionsHeading="`${userName}'s Interests`" :initial-skills="mappedAvailableSkills"
            v-model:interests="mappedUserInterests" :on-save="handleSaveInterests" :loading="loading"
            :isSaving="isSaving" />
    </div>
</template>

<script setup>
import TagSelector from "@/components/common/TagSelector.vue";
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

const props = defineProps({
    locationID: {
        type: String,
        default: null
    },
    userID: {
        type: String,
        default: null
    }
});

const store = useStore();
const route = useRoute();

const loading = ref(true);
const isSaving = ref(false);

const locationId = props.locationID || route.params.locationID;
const personId = props.userID || route.params.userID;

// 🧠 Vuex data
const availableSkills = computed(() => store.getters.availableSkills);
const userInterests = computed(() => store.getters.userInterestSkills);
const assignedSkills = computed(() => store.getters.userSkills);
const storeLoading = computed(() => store.getters.openPositionsLoading);

// Watch for store loading state and available skills to update local loading
watch([storeLoading, availableSkills], ([isStoreLoading, skills]) => {
    if (!isStoreLoading && skills) {
        loading.value = false;
    }
}, { immediate: true });

const userName = ref("User");

// ✅ My Interests mapping (exclude skills the user is already assigned to)
const assignedSkillIds = computed(() =>
    (assignedSkills.value || []).map(s => s.skillid)
);

const mappedAvailableSkills = computed(() =>
    availableSkills.value
        .filter(skill => !assignedSkillIds.value.includes(skill.skillid) && skill.active !== false)
        .map(skill => ({
            id: skill.skillid,
            name: skill.skillname,
            description: skill.description ?? ""
        }))
);

const mappedUserInterests = computed({
    get: () => userInterests.value
        .filter(skill => !assignedSkillIds.value.includes(skill.skillid))
        .map(skill => ({
        id: skill.skillid,
        name: skill.skillname,
        description: skill.description ?? ""
    })),
    set: (newInterests) => {
        store.commit("SET_USER_INTERESTS", newInterests.map(interest => ({
            skillid: interest.id,
            skillname: interest.name,
            description: interest.description
        })));
    }
});

// ✅ Save handler for interests
const handleSaveInterests = async (selectedInterests) => {
    isSaving.value = true;
    try {
        const skillIds = selectedInterests.map(interest => interest.id);
        await store.dispatch("saveInterestSkills", {
            locationId,
            personId,
            skills: skillIds
        });
    } finally {
        isSaving.value = false;
    }
};
</script>

